import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

export interface StreakFreezeData {
  freezeTokens: number;
  freezeUsedDate: string | null;
  totalFreezesUsed: number;
}

export const useStreakFreeze = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  const useFreezeToken = useCallback(async (): Promise<boolean> => {
    if (!user) {
      toast.error('You must be logged in to use a streak freeze');
      return false;
    }

    setLoading(true);
    try {
      // Fetch current streak data
      const { data: streakData, error: fetchError } = await supabase
        .from('user_streaks')
        .select('freeze_tokens, freeze_used_date, total_freezes_used, current_streak')
        .eq('user_id', user.id)
        .single();

      if (fetchError) {
        throw fetchError;
      }

      if (!streakData || streakData.freeze_tokens <= 0) {
        toast.error('No freeze tokens available');
        return false;
      }

      const today = new Date().toISOString().split('T')[0];
      
      // Check if freeze was already used today
      if (streakData.freeze_used_date === today) {
        toast.error('Freeze already used today');
        return false;
      }

      // Use the freeze token
      const { error: updateError } = await supabase
        .from('user_streaks')
        .update({
          freeze_tokens: streakData.freeze_tokens - 1,
          freeze_used_date: today,
          total_freezes_used: (streakData.total_freezes_used || 0) + 1,
          last_active_date: today, // Keep streak active
        })
        .eq('user_id', user.id);

      if (updateError) {
        throw updateError;
      }

      toast.success('🧊 Streak freeze used! Your streak is protected for today.');
      return true;
    } catch (error) {
      console.error('Error using freeze token:', error);
      toast.error('Failed to use streak freeze');
      return false;
    } finally {
      setLoading(false);
    }
  }, [user]);

  const getFreezeData = useCallback(async (): Promise<StreakFreezeData | null> => {
    if (!user) return null;

    try {
      const { data, error } = await supabase
        .from('user_streaks')
        .select('freeze_tokens, freeze_used_date, total_freezes_used')
        .eq('user_id', user.id)
        .single();

      if (error) {
        console.error('Error fetching freeze data:', error);
        return null;
      }

      return {
        freezeTokens: data?.freeze_tokens ?? 3,
        freezeUsedDate: data?.freeze_used_date ?? null,
        totalFreezesUsed: data?.total_freezes_used ?? 0,
      };
    } catch (error) {
      console.error('Error:', error);
      return null;
    }
  }, [user]);

  return {
    useFreezeToken,
    getFreezeData,
    loading,
  };
};
